'use client'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import {
	HYPERLIQUID_BRIDGE2_ARBITRUM,
	HYPERLIQUID_MIN_DEPOSIT_USDC,
	HYPERLIQUID_MIN_ORDER_NOTIONAL_USD,
} from '@/constants/hyperliquid'
import { useHyperliquidBalance } from '@/hooks/useHyperliquidBalance'
import { useHyperliquidDeposit } from '@/hooks/useHyperliquidDeposit'
import {
	isDepositLocked,
	releaseDepositLock,
	tryClaimDepositLock,
} from '@/lib/hyperliquid/deposit-lock'
import { useModalStore } from '@/stores/modal-store'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

const ARBITRUM_EXPLORER = 'https://arbiscan.io/tx/'

export function HyperliquidDepositModal() {
	const { activeModal, closeModal, openModal } = useModalStore()
	const isOpen = activeModal === 'hyperliquid-deposit'
	const [amount, setAmount] = useState<string>(String(HYPERLIQUID_MIN_DEPOSIT_USDC))
	const deposit = useHyperliquidDeposit()
	const balance = useHyperliquidBalance(isOpen)
	const initialBalanceRef = useRef<string | null>(null)

	// Capture balance at open so the "Credited" banner can detect arrival.
	useEffect(() => {
		if (isOpen && balance.data && initialBalanceRef.current === null) {
			initialBalanceRef.current = balance.data.withdrawable
		}
		if (!isOpen) initialBalanceRef.current = null
	}, [isOpen, balance.data])

	const credited =
		initialBalanceRef.current !== null &&
		balance.data &&
		Number(balance.data.withdrawable) > Number(initialBalanceRef.current)

	const back = () => {
		closeModal()
		openModal('deposit-primary')
	}

	const amountNum = Number.parseFloat(amount)
	const valid = Number.isFinite(amountNum) && amountNum >= HYPERLIQUID_MIN_DEPOSIT_USDC

	const handleDeposit = () => {
		// Coordinate with the auto-deposit watcher: if the watcher is in the
		// middle of its own Bridge2 deposit, a second userOp here would race
		// the EOA balance and revert with "ERC20 transfer amount exceeds
		// balance". The lock is shared module state (deposit-lock.ts).
		if (!tryClaimDepositLock()) return
		deposit.mutate(
			{ amountUsdc: amountNum },
			{
				onSettled: () => releaseDepositLock(),
			}
		)
	}

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && closeModal()}>
			<DialogContent
				closeButtonClasses="size-[24px] text-white"
				closeButtonContainerClasses="top-[20px] right-[26px] cursor-pointer"
				className="gap-[20px] rounded-3xl border border-white/10 bg-brand-dark-light p-[24px] sm:max-w-[440px]"
			>
				<DialogHeader className="flex flex-col gap-[8px]">
					<button
						type="button"
						className="-mt-1 -ml-1 flex w-fit items-center gap-1 self-start text-brand-muted text-xs hover:text-white"
						onClick={back}
					>
						<ArrowLeft className="h-3 w-3" /> Back
					</button>
					<DialogTitle className="text-center font-semibold text-[18px] text-white">
						Deposit to Hyperliquid
					</DialogTitle>
					<DialogDescription className="text-center text-[14px] text-brand-muted">
						USDC on Arbitrum → Hyperliquid Bridge2. Credit lands in ~3–4min.
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-[14px]">
					<label className="flex flex-col gap-1">
						<span className="text-brand-muted text-xs">
							Amount (USDC, min {HYPERLIQUID_MIN_DEPOSIT_USDC})
						</span>
						<input
							type="number"
							inputMode="decimal"
							min={HYPERLIQUID_MIN_DEPOSIT_USDC}
							step={1}
							value={amount}
							onChange={e => setAmount(e.target.value)}
							className="rounded-lg border border-white/10 bg-brand-dark px-3 py-2 text-white outline-none focus:border-brand-green/60"
						/>
					</label>

					{/* Two minimums in play. Bridge2 silently drops < $5 deposits,
					    but HL outcome-market orders have a hard $10 notional floor.
					    Depositing $5-$9 successfully credits HL but the funds sit
					    untradeable until topped up — surface that here so the user
					    doesn't discover it after the 4-min validator wait. */}
					{Number.isFinite(amountNum) &&
						amountNum >= HYPERLIQUID_MIN_DEPOSIT_USDC &&
						amountNum < HYPERLIQUID_MIN_ORDER_NOTIONAL_USD && (
							<div className="rounded-md border border-yellow-500/40 bg-yellow-500/5 px-3 py-2 text-xs text-yellow-200">
								HL outcome markets require ${HYPERLIQUID_MIN_ORDER_NOTIONAL_USD} minimum per order.
								Deposit ${HYPERLIQUID_MIN_ORDER_NOTIONAL_USD}+ to trade — ${amountNum} will land on
								HL but can't be used until topped up.
							</div>
						)}

					<div className="rounded-lg border border-white/5 bg-brand-dark/60 px-3 py-2 text-brand-muted text-xs">
						<div className="flex justify-between">
							<span>Source chain</span>
							<span className="text-white">Arbitrum One</span>
						</div>
						<div className="flex justify-between">
							<span>Bridge contract</span>
							<a
								href={`https://arbiscan.io/address/${HYPERLIQUID_BRIDGE2_ARBITRUM}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-white underline-offset-2 hover:underline"
							>
								{HYPERLIQUID_BRIDGE2_ARBITRUM.slice(0, 8)}…{HYPERLIQUID_BRIDGE2_ARBITRUM.slice(-4)}
							</a>
						</div>
						<div className="flex justify-between">
							<span>Steps</span>
							<span className="text-white">approve → deposit</span>
						</div>
					</div>

					{deposit.error && (
						<div className="rounded-md border border-brand-red/40 bg-brand-red/10 px-3 py-2 text-brand-red text-xs">
							{(deposit.error as Error).message}
						</div>
					)}

					{deposit.data && (
						<div className="rounded-md border border-white/10 bg-brand-dark/60 px-3 py-2 text-xs">
							<div className="flex justify-between">
								<span className="text-brand-muted">tx</span>
								<a
									href={`${ARBITRUM_EXPLORER}${deposit.data.txHash}`}
									target="_blank"
									rel="noopener noreferrer"
									className="text-white hover:underline"
								>
									{deposit.data.txHash.slice(0, 10)}…
								</a>
							</div>
							<div className="mt-2 text-brand-muted">
								{credited ? '✓ Credited to Hyperliquid' : 'Waiting for bridge credit (~3–4min)…'}
							</div>
						</div>
					)}

					<Button
						type="button"
						disabled={!valid || deposit.isPending || isDepositLocked()}
						onClick={handleDeposit}
						className="h-11 rounded-lg bg-brand-green font-medium text-brand-dark hover:bg-brand-green/90 disabled:opacity-40"
					>
						{deposit.isPending
							? 'Sending approve + deposit…'
							: deposit.isSuccess && !credited
								? 'Waiting for bridge…'
								: deposit.isSuccess && credited
									? 'Credited'
									: `Deposit ${amount || '0'} USDC`}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
